import { api } from '$lib/shared/api/client';
import type { CreateProfileRequest, UserProfile } from '$lib/shared/types/api';

/** The viewer's IANA time zone, so date filters and exports cover their own calendar days. */
export function viewerTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Get the authenticated user's profile
 * @returns Promise<UserProfile>
 */
export async function getMyProfile(): Promise<UserProfile> {
  return api.get<UserProfile>('/api/profile');
}

/**
 * Create the authenticated user's profile (first-time onboarding).
 */
export async function createMyProfile(payload: CreateProfileRequest): Promise<UserProfile> {
  return api.post<UserProfile>('/api/profile', payload);
}

/**
 * Update the authenticated user's profile
 * @param profile - Partial profile data to update
 * @returns Promise<UserProfile>
 */
export async function updateMyProfile(profile: Partial<UserProfile>): Promise<UserProfile> {
  return api.put<UserProfile>('/api/profile', profile);
}

/** Upload a new profile photo (multipart POST /api/profile/image). */
export async function uploadProfileImage(file: File): Promise<void> {
  const formData = new FormData();
  formData.append('File', file);
  await api.postForm('/api/profile/image', formData);
}

/** Presigned-PUT upload for organisation images; kind maps to the API's profile/banner key prefixes. */
async function uploadOrganisationImageOfKind(
  organisationId: string,
  file: File,
  kind: 'profile' | 'banner'
): Promise<void> {
  const { key, uploadUrl } = await api.post<{ key: string; uploadUrl: string }>(
    `/api/organisation/${organisationId}/image-upload-url`,
    { fileName: file.name, contentType: file.type, fileSize: file.size, kind }
  );

  // Raw fetch — the presigned S3 PUT must not carry the API auth header.
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!uploadResponse.ok) {
    throw new Error(`Image upload failed (${uploadResponse.status})`);
  }

  const formData = new FormData();
  formData.append('OrganisationId', organisationId);
  formData.append('Key', key);
  await api.putForm(
    kind === 'banner' ? '/api/organisation/uploadBannerImage' : '/api/organisation/uploadImage',
    formData
  );
}

/** Upload/replace an organisation logo via the presigned-PUT flow (no multipart size limit). */
export async function uploadOrganisationImage(organisationId: string, file: File): Promise<void> {
  return uploadOrganisationImageOfKind(organisationId, file, 'profile');
}

/** Upload/replace an organisation banner image via the presigned-PUT flow. */
export async function uploadOrganisationBannerImage(organisationId: string, file: File): Promise<void> {
  return uploadOrganisationImageOfKind(organisationId, file, 'banner');
}

/**
 * Get all users (admin only)
 * @returns Promise<UserProfile[]>
 */
export async function getAllUsers(): Promise<UserProfile[]> {
  return api.get<UserProfile[]>('/api/management/users');
}

/**
 * Get all users with matching answers data (admin only)
 * @returns Promise<MatchingAnswersResponse[]>
 */
export async function getAllUsersWithMatchingAnswers(): Promise<import('$shared/types/api').MatchingAnswersResponse[]> {
  return api.get<import('$shared/types/api').MatchingAnswersResponse[]>('/api/management/users');
}

/**
 * Get a specific profile by ID (authenticated)
 * @param profileId - The profile ID
 * @returns Promise<UserProfile>
 */
export async function getProfileById(profileId: string): Promise<UserProfile> {
  return api.get<UserProfile>(`/api/profile/${profileId}`);
}

/**
 * Get a specific user's profile by ID (admin only)
 * @param userId - The user's ID
 * @returns Promise<UserProfile>
 */
export async function getUserProfile(userId: string): Promise<UserProfile> {
  return api.get<UserProfile>(`/api/management/users/${userId}`);
}

/**
 * Update a user's profile by ID (admin only)
 * @param userId - The user's ID
 * @param profile - Partial profile data to update
 * @returns Promise<UserProfile>
 */
export async function updateUserProfile(userId: string, profile: Partial<UserProfile>): Promise<UserProfile> {
  return api.put<UserProfile>(`/api/management/users/${userId}`, profile);
}

/**
 * Get all organisations (admin only)
 * @returns Promise<Organisation[]>
 */
export async function getAllOrganisations(): Promise<import('$shared/types/api').Organisation[]> {
  return api.get<import('$shared/types/api').Organisation[]>('/api/management/organisations');
}

/**
 * Get all organisations with interested users (admin only)
 * @returns Promise<OrganisationWithInterests[]>
 */
export async function getAllOrganisationsWithInterests(): Promise<
  import('$shared/types/api').OrganisationWithInterests[]
> {
  return api.get<import('$shared/types/api').OrganisationWithInterests[]>('/api/management/organisations');
}

/**
 * Get a specific organisation by ID (for members of the organisation)
 * @param organisationId - The organisation's ID
 * @returns Promise<Organisation>
 */
export async function getOrganisation(organisationId: string): Promise<import('$shared/types/api').Organisation> {
  return api.get<import('$shared/types/api').Organisation>(`/api/organisation/${organisationId}`);
}

export async function getOrganisationDirectoryUsers(
  organisationId: string,
  params?: {
    search?: string;
    source?: import('$shared/types/api').OrganisationDirectorySource;
    page?: number;
    pageSize?: number;
  }
): Promise<import('$shared/types/api').OrganisationDirectoryUsersPage> {
  const q = new URLSearchParams();
  if (params?.search?.trim()) q.set('search', params.search.trim());
  if (params?.source) q.set('source', params.source);
  if (params?.page) q.set('page', String(params.page));
  if (params?.pageSize) q.set('pageSize', String(params.pageSize));
  const qs = q.toString();
  return api.get<import('$shared/types/api').OrganisationDirectoryUsersPage>(
    `/api/organisation/${organisationId}/directory-users${qs ? `?${qs}` : ''}`
  );
}

export async function getOrganisationUserHistory(
  organisationId: string,
  userId: string
): Promise<import('$shared/types/api').OrganisationUserHistory> {
  return api.get<import('$shared/types/api').OrganisationUserHistory>(
    `/api/organisation/${organisationId}/users/${userId}/history`
  );
}

/**
 * Update an organisation by ID (admin only)
 * @param organisationId - The organisation's ID
 * @param organisation - Partial organisation data to update
 * @returns Promise<Organisation>
 */
export async function updateOrganisation(
  organisationId: string,
  organisation: Partial<import('$shared/types/api').Organisation>
): Promise<import('$shared/types/api').Organisation> {
  return api.put<import('$shared/types/api').Organisation>(
    `/api/management/organisations/${organisationId}`,
    organisation
  );
}

/**
 * Get connection overview data (admin only)
 * @returns Promise<ConnectionOverviewResponse>
 */
export async function getConnectionOverview(): Promise<import('$shared/types/api').ConnectionOverviewResponse> {
  return api.get<import('$shared/types/api').ConnectionOverviewResponse>('/api/management/connection-overview');
}

export async function manualConnectHealthcare(params: {
  practitionerId: string;
  organisationIds: string[];
  override?: boolean;
}): Promise<void> {
  await api.post('/api/management/manual-connect-healthcare', {
    practitionerId: params.practitionerId,
    organisationId: params.organisationIds,
    override: params.override ?? false,
  });
}

/**
 * Get organisation payment methods
 * @param organisationId - The organisation's ID
 * @returns Promise<OrganisationPaymentMethod>
 */
export async function getOrganisationPaymentMethods(
  organisationId: string
): Promise<import('$shared/types/api').OrganisationPaymentMethod> {
  return api.get<import('$shared/types/api').OrganisationPaymentMethod>(
    `/api/organisation/${organisationId}/get-payment-methods`
  );
}

/**
 * Update organisation details as a member (PUT /api/organisation/{id}).
 * Requires owner, administrator, or payment administrator role.
 */
export async function updateMemberOrganisation(
  organisationId: string,
  payload: import('$shared/types/api').UpdateMemberOrganisationRequest
): Promise<import('$shared/types/api').Organisation> {
  return api.put<import('$shared/types/api').Organisation>(`/api/organisation/${organisationId}`, {
    id: organisationId,
    ...payload,
  });
}

/** Stripe setup intent for adding or managing organisation payment methods. */
export async function createOrganisationPaymentIntent(
  organisationId: string
): Promise<import('$shared/types/api').OrganisationPaymentIntentResponse> {
  return api.get<import('$shared/types/api').OrganisationPaymentIntentResponse>(
    `/api/organisation/${organisationId}/create-organisation-payment-intent`
  );
}

/** Retry charging an outstanding consultation; re-sending the same key never charges twice. */
export async function retryOrganisationPayment(
  organisationId: string,
  consultationId: string,
  idempotencyKey?: string
): Promise<void> {
  await api.post(
    `/api/organisation/${organisationId}/retry-payment/${consultationId}`,
    idempotencyKey ? { idempotencyKey } : {}
  );
}

/** Remove a saved organisation payment method. Requires more than one method on file. */
export async function removeOrganisationPaymentMethod(organisationId: string, paymentMethodId: string): Promise<void> {
  await api.delete(`/api/organisation/${organisationId}/payment-methods/${paymentMethodId}`);
}

export async function getOrganisationCreditWallet(
  organisationId: string
): Promise<import('$shared/types/api').OrganisationCreditWallet> {
  return api.get<import('$shared/types/api').OrganisationCreditWallet>(
    `/api/organisation/${organisationId}/credit-wallet`
  );
}

export async function getOrganisationCreditTransactions(
  organisationId: string,
  params?: { page?: number; pageSize?: number; type?: string; from?: string; to?: string }
): Promise<import('$shared/types/api').OrganisationCreditTransactionsResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.type) queryParams.append('type', params.type);
  if (params?.from) queryParams.append('from', params.from);
  if (params?.to) queryParams.append('to', params.to);

  const queryString = queryParams.toString();
  const url = `/api/organisation/${organisationId}/credit-transactions${queryString ? `?${queryString}` : ''}`;
  return api.get<import('$shared/types/api').OrganisationCreditTransactionsResponse>(url);
}

/** Charge the card on file and add credit; re-sending the same key returns the original recharge. */
export async function addOrganisationCredit(
  organisationId: string,
  amount: number,
  idempotencyKey?: string
): Promise<import('$shared/types/api').AddOrganisationCreditResponse> {
  return api.post<import('$shared/types/api').AddOrganisationCreditResponse>(
    `/api/organisation/${organisationId}/credit-recharge`,
    {
      amount,
      currency: 'AUD',
      ...(idempotencyKey ? { idempotencyKey } : {}),
    }
  );
}

export async function getOrganisationCampaigns(
  organisationId: string
): Promise<import('$shared/types/api').Campaign[]> {
  return api.get<import('$shared/types/api').Campaign[]>(`/api/campaign/organisation/${organisationId}`);
}

export async function getCampaignById(campaignId: string): Promise<import('$shared/types/api').Campaign> {
  return api.get<import('$shared/types/api').Campaign>(`/api/campaign/${campaignId}`);
}

export async function getOrganisationCampaignResponses(
  organisationId: string,
  params?: { completedOnly?: boolean }
): Promise<import('$shared/types/api').CampaignResponseSummary[]> {
  const qs = params?.completedOnly ? '?completedOnly=true' : '';
  return api.get<import('$shared/types/api').CampaignResponseSummary[]>(
    `/api/campaign/organisation/${organisationId}/responses${qs}`
  );
}

export async function getCampaignResponseById(
  responseId: string
): Promise<import('$shared/types/api').CampaignResponseDetail> {
  return api.get<import('$shared/types/api').CampaignResponseDetail>(`/api/campaign/response/${responseId}`);
}

export async function getCampaignResponses(
  campaignId: string
): Promise<import('$shared/types/api').CampaignResponseDetail[]> {
  return api.get<import('$shared/types/api').CampaignResponseDetail[]>(`/api/campaign/${campaignId}/responses`);
}

export async function getOrganisationCampaignFollowupContacts(
  organisationId: string,
  params?: {
    campaignId?: string;
    search?: string;
    profession?: string;
    from?: string;
    to?: string;
    sortBy?: import('$shared/types/api').CampaignFollowupSortField;
    sortDescending?: boolean;
    page?: number;
    pageSize?: number;
  }
): Promise<import('$shared/types/api').CampaignFollowupContactsPage> {
  const q = new URLSearchParams();
  if (params?.campaignId) q.set('campaignId', params.campaignId);
  if (params?.search?.trim()) q.set('search', params.search.trim());
  if (params?.profession?.trim()) q.set('profession', params.profession.trim());
  if (params?.from) q.set('from', params.from);
  if (params?.to) q.set('to', params.to);
  const timeZone = params?.from || params?.to ? viewerTimeZone() : undefined;
  if (timeZone) q.set('timeZone', timeZone);
  if (params?.sortBy) q.set('sortBy', params.sortBy);
  if (params?.sortDescending !== undefined) q.set('sortDescending', params.sortDescending ? 'true' : 'false');
  if (params?.page) q.set('page', String(params.page));
  if (params?.pageSize) q.set('pageSize', String(params.pageSize));
  const qs = q.toString();
  const url = `/api/campaign/organisation/${organisationId}/followup-contacts${qs ? `?${qs}` : ''}`;
  return api.get<import('$shared/types/api').CampaignFollowupContactsPage>(url);
}

export async function getPrimaryProfessions(): Promise<import('$shared/types/api').PrimaryProfessionOption[]> {
  return api.get<import('$shared/types/api').PrimaryProfessionOption[]>(
    '/api/app-data/list-primary-professions?filter=all',
    {
      skipAuth: true,
    }
  );
}

export async function createOrganisationCampaign(
  organisationId: string,
  campaign: import('$shared/types/api').CreateOrganisationCampaignRequest
): Promise<import('$shared/types/api').Campaign> {
  return api.post<import('$shared/types/api').Campaign>(`/api/campaign/organisation/${organisationId}`, campaign);
}

export async function generateCampaignSummary(
  organisationId: string,
  request: import('$shared/types/api').GenerateCampaignSummaryRequest
): Promise<import('$shared/types/api').GenerateCampaignSummaryResponse> {
  return api.post<import('$shared/types/api').GenerateCampaignSummaryResponse>(
    `/api/campaign/organisation/${organisationId}/generate-summary`,
    request
  );
}

export async function generateCampaignQuestion(
  organisationId: string,
  request: import('$shared/types/api').GenerateCampaignQuestionRequest
): Promise<import('$shared/types/api').GenerateCampaignQuestionResponse> {
  return api.post<import('$shared/types/api').GenerateCampaignQuestionResponse>(
    `/api/campaign/organisation/${organisationId}/generate-question`,
    request
  );
}

export async function generateOrganisationCampaignFromAi(
  organisationId: string,
  request: import('$shared/types/api').GenerateOrganisationCampaignFromAiRequest
): Promise<import('$shared/types/api').Campaign> {
  return api.post<import('$shared/types/api').Campaign>(
    `/api/campaign/organisation/${organisationId}/generate-from-ai`,
    request
  );
}

export async function updateCampaign(
  campaignId: string,
  campaign: import('$shared/types/api').UpdateOrganisationCampaignRequest
): Promise<import('$shared/types/api').Campaign> {
  return api.put<import('$shared/types/api').Campaign>(`/api/campaign/${campaignId}`, campaign);
}

export async function startCampaign(campaignId: string): Promise<import('$shared/types/api').Campaign> {
  return api.post<import('$shared/types/api').Campaign>(`/api/campaign/${campaignId}/start`);
}

export async function addCampaignFunds(
  campaignId: string,
  amount: number
): Promise<import('$shared/types/api').Campaign> {
  return api.post<import('$shared/types/api').Campaign>(`/api/campaign/${campaignId}/funds`, { amount });
}

export async function pauseCampaign(campaignId: string): Promise<import('$shared/types/api').Campaign> {
  return api.post<import('$shared/types/api').Campaign>(`/api/campaign/${campaignId}/pause`);
}

export async function endCampaign(campaignId: string): Promise<import('$shared/types/api').Campaign> {
  return api.post<import('$shared/types/api').Campaign>(`/api/campaign/${campaignId}/end`);
}

export async function deleteCampaign(campaignId: string): Promise<import('$shared/types/api').Campaign> {
  return api.post<import('$shared/types/api').Campaign>(`/api/campaign/${campaignId}/delete`);
}

/**
 * Get organisation consultations
 * @param organisationId - The organisation's ID
 * @param params - Query parameters for filtering
 * @returns Promise<Consultation[]>
 */
export async function getOrganisationConsultations(
  organisationId: string,
  params?: {
    searchQuery?: string;
    consultationType?: string;
    startDate?: string;
    endDate?: string;
    primaryProfessions?: string[];
    status?: string;
    page?: number;
    pageSize?: number;
  }
): Promise<import('$shared/types/api').OrganisationConsultationsPage> {
  const queryParams = new URLSearchParams();
  if (params?.searchQuery) queryParams.append('searchQuery', params.searchQuery);
  if (params?.consultationType) queryParams.append('consultationType', params.consultationType);
  if (params?.startDate) queryParams.append('startDate', params.startDate);
  if (params?.endDate) queryParams.append('endDate', params.endDate);
  if (params?.primaryProfessions) {
    params.primaryProfessions.forEach((prof, i) => {
      queryParams.append(`primaryProfessions[${i}]`, prof);
    });
  }
  if (params?.status) queryParams.append('status', params.status);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());

  const queryString = queryParams.toString();
  const url = `/api/organisation/${organisationId}/consultations${queryString ? `?${queryString}` : ''}`;
  return api.get<import('$shared/types/api').OrganisationConsultationsPage>(url);
}

export async function getOrganisationConsultation(
  organisationId: string,
  consultationId: string
): Promise<import('$shared/types/api').Consultation> {
  return api.get<import('$shared/types/api').Consultation>(
    `/api/organisation/${organisationId}/consultations/${consultationId}`
  );
}

/** Transfer of value CSV: dates are inclusive YYYY-MM-DD days in the viewer's time zone; omit both for no limit. */
export async function exportOrganisationConsultations(
  organisationId: string,
  range: { startDate?: string; endDate?: string } = {}
): Promise<string> {
  const body: { startDate?: string; endDate?: string; timeZone?: string } = {};
  if (range.startDate) body.startDate = range.startDate;
  if (range.endDate) body.endDate = range.endDate;
  if (range.startDate || range.endDate) body.timeZone = viewerTimeZone();

  // A read sent as a POST, so it can be retried like a GET.
  return api.post<string>(`/api/organisation/${organisationId}/consultations/export`, body, {
    retry: true,
    headers: { Accept: 'text/csv' },
  });
}
